// Page de détail d'un post
// Affichage complet avec commentaires

import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, MessageCircle, Share2, Bookmark, ChevronLeft,
  Send, MoreHorizontal, MapPin, ChevronRight
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Comment Item
function CommentItem({ comment, onReply }) {
  const [showReplies, setShowReplies] = useState(false);
  
  return (
    <div className="py-3">
      <div className="flex gap-3">
        {comment.author?.profile_photo ? (
          <img
            src={comment.author.profile_photo}
            alt=""
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <span className="text-gray-500 text-sm font-semibold">
              {comment.author?.display_name?.[0]?.toUpperCase()}
            </span>
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <p>
            <Link 
              to={`/reseau/profil/${comment.author?.username}`}
              className="font-semibold text-gray-900 mr-2 hover:underline"
            >
              {comment.author?.display_name}
            </Link>
            <span className="text-gray-700">{comment.content}</span>
          </p>
          
          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
            <span>{formatTimeAgo(comment.created_at)}</span>
            {comment.likes_count > 0 && (
              <span>{comment.likes_count} j'aime{comment.likes_count > 1 ? 's' : ''}</span>
            )}
            <button 
              onClick={() => onReply?.(comment)}
              className="hover:text-gray-700"
            >
              Répondre
            </button>
          </div>
          
          {/* Replies */}
          {comment.replies?.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {showReplies ? 'Masquer' : `Voir les ${comment.replies.length} réponses`}
              </button>
              
              {showReplies && (
                <div className="mt-2 pl-4 border-l-2 border-gray-100">
                  {comment.replies.map((reply) => (
                    <div key={reply.id} className="py-2">
                      <p>
                        <Link 
                          to={`/reseau/profil/${reply.author?.username}`}
                          className="font-semibold text-gray-900 mr-2 hover:underline"
                        >
                          {reply.author?.display_name}
                        </Link>
                        <span className="text-gray-700">{reply.content}</span>
                      </p>
                      <span className="text-xs text-gray-500">
                        {formatTimeAgo(reply.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} j`;
  return date.toLocaleDateString('fr-FR');
}

export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { profile } = useOutletContext() || {};
  const commentInputRef = useRef(null);
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('ecosystem_token');

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/posts/${postId}`);
      if (res.ok) {
        const data = await res.json();
        setPost(data);
        setLikesCount(data.likes_count || 0);
      } else {
        toast.error('Post non trouvé');
        navigate('/reseau');
      }
    } catch (e) {
      console.error('Error fetching post:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/posts/${postId}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(data);
      }
    } catch (e) {
      console.error('Error fetching comments:', e);
    }
  };

  const handleLike = async () => {
    if (!token) {
      toast.error('Connectez-vous pour aimer ce post');
      return;
    }
    
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    
    try {
      const method = isLiked ? 'DELETE' : 'POST';
      await fetch(`${API_URL}/api/ecosystem/posts/${postId}/like`, {
        method,
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Error liking post:', e);
    }
  };

  const handleSave = async () => {
    if (!token) {
      toast.error('Connectez-vous pour sauvegarder ce post');
      return;
    }
    
    setIsSaved(!isSaved);
    
    try {
      await fetch(`${API_URL}/api/ecosystem/posts/${postId}/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Error saving post:', e);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Connectez-vous pour commenter');
      return;
    }
    
    if (!newComment.trim()) return;
    
    setSending(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newComment.trim(),
          parent_id: replyTo?.id || null
        })
      });
      
      if (res.ok) {
        setNewComment('');
        setReplyTo(null);
        fetchComments();
        toast.success('Commentaire ajouté');
      }
    } catch (e) {
      console.error('Error commenting:', e);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setNewComment(`@${comment.author?.username} `);
    commentInputRef.current?.focus();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-white md:bg-gray-50">
      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center h-14 px-4">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ChevronLeft size={24} />
          </button>
          <h1 className="font-semibold text-gray-900 ml-2">Publication</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto md:py-8 md:px-4">
        <div className="bg-white md:rounded-2xl md:shadow-lg overflow-hidden md:flex">
          {/* Media Section */}
          {post.media && post.media.length > 0 && (
            <div className="md:w-1/2 relative bg-black">
              <div className="aspect-square md:aspect-auto md:h-full">
                {post.media[currentMediaIndex]?.type === 'video' ? (
                  <video
                    src={post.media[currentMediaIndex].url}
                    className="w-full h-full object-contain"
                    controls
                    playsInline
                  />
                ) : (
                  <img
                    src={post.media[currentMediaIndex]?.url}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                )}
                
                {post.media.length > 1 && (
                  <>
                    {currentMediaIndex > 0 && (
                      <button
                        onClick={() => setCurrentMediaIndex(prev => prev - 1)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"
                      >
                        <ChevronLeft size={20} />
                      </button>
                    )}
                    {currentMediaIndex < post.media.length - 1 && (
                      <button
                        onClick={() => setCurrentMediaIndex(prev => prev + 1)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"
                      >
                        <ChevronRight size={20} />
                      </button>
                    )}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                      {post.media.map((_, idx) => (
                        <div
                          key={idx}
                          className={`w-1.5 h-1.5 rounded-full ${
                            idx === currentMediaIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Content Section */}
          <div className="md:w-1/2 flex flex-col max-h-[80vh]">
            {/* Author Header */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <Link to={`/reseau/profil/${post.author?.username}`}>
                {post.author?.profile_photo ? (
                  <img
                    src={post.author.profile_photo}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">
                      {post.author?.display_name?.[0]?.toUpperCase()}
                    </span>
                  </div>
                )}
              </Link>
              
              <div className="flex-1">
                <Link 
                  to={`/reseau/profil/${post.author?.username}`}
                  className="font-semibold text-gray-900 hover:underline flex items-center gap-1"
                >
                  {post.author?.display_name}
                  {post.author?.is_verified && (
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </Link>
                {post.location && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin size={12} />
                    {post.location}
                  </p>
                )}
              </div>
              
              <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                <MoreHorizontal size={20} />
              </button>
            </div>
            
            {/* Comments Section */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Post Content */}
              {post.content && (
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <p className="text-gray-800 whitespace-pre-wrap">
                    <Link 
                      to={`/reseau/profil/${post.author?.username}`}
                      className="font-semibold mr-2"
                    >
                      {post.author?.display_name}
                    </Link>
                    {post.content}
                  </p>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {post.tags.map((tag, idx) => (
                        <Link
                          key={idx}
                          to={`/reseau/recherche?q=${tag}`}
                          className="text-blue-500 hover:text-blue-600"
                        >
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Comments */}
              {comments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Aucun commentaire. Soyez le premier !
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {comments.map((comment) => (
                    <CommentItem 
                      key={comment.id} 
                      comment={comment}
                      onReply={handleReply}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="border-t border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleLike}
                    className={isLiked ? 'text-red-500' : 'text-gray-700 hover:text-red-500'}
                  >
                    <Heart size={24} fill={isLiked ? 'currentColor' : 'none'} />
                  </button>
                  <button 
                    onClick={() => commentInputRef.current?.focus()}
                    className="text-gray-700 hover:text-blue-500"
                  >
                    <MessageCircle size={24} />
                  </button>
                  <button className="text-gray-700 hover:text-green-500">
                    <Share2 size={24} />
                  </button>
                </div>
                <button
                  onClick={handleSave}
                  className={isSaved ? 'text-yellow-500' : 'text-gray-700 hover:text-yellow-500'}
                >
                  <Bookmark size={24} fill={isSaved ? 'currentColor' : 'none'} />
                </button>
              </div>
              
              {likesCount > 0 && (
                <p className="font-semibold text-gray-900 mb-2">
                  {likesCount.toLocaleString()} j'aime{likesCount > 1 ? 's' : ''}
                </p>
              )}
              
              <p className="text-xs text-gray-500 mb-4">
                {formatTimeAgo(post.created_at)}
              </p>
              
              {/* Reply indicator */}
              {replyTo && (
                <div className="mb-2 text-sm text-gray-500 flex items-center gap-2">
                  <span>Réponse à @{replyTo.author?.username}</span>
                  <button
                    onClick={() => {
                      setReplyTo(null);
                      setNewComment('');
                    }}
                    className="text-red-500 hover:text-red-600"
                  >
                    Annuler
                  </button>
                </div>
              )}
              
              {/* Comment Input */}
              <form onSubmit={handleComment} className="flex items-center gap-2">
                <Input
                  ref={commentInputRef}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  className="flex-1"
                  data-testid="comment-input"
                />
                <Button
                  type="submit"
                  disabled={!newComment.trim() || sending}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="icon"
                >
                  <Send size={18} />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
